import { config } from '@/core/config'

const isNotDevelopmentEnv = config.mode !== 'development'
const isNotCrawlerBot = navigator.userAgent.indexOf('Pinterestbot') === -1

let SentryInstance = null

// Retire les donnees personnelles des adresses avant tout envoi a Sentry.
const PERSONAL_QUERY_KEYS = ['email', 'loginHint', 'login_hint']

function maskUrl(url) {
  if (typeof url !== 'string' || url.indexOf('=') === -1) {
    return url
  }

  let masked = url

  PERSONAL_QUERY_KEYS.forEach((key) => {
    masked = masked.replace(new RegExp(`([?&]${key}=)[^&#]*`, 'gi'), '$1[masque]')
  })

  return masked
}

function maskPersonalData(event) {
  try {
    if (event.request) {
      if (event.request.url) {
        event.request.url = maskUrl(event.request.url)
      }

      if (event.request.query_string) {
        event.request.query_string = maskUrl(`?${event.request.query_string}`).slice(1)
      }
    }

    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs.forEach((breadcrumb) => {
        if (breadcrumb && breadcrumb.data && breadcrumb.data.url) {
          breadcrumb.data.url = maskUrl(breadcrumb.data.url)
        }
      })
    }
  } catch (e) {
    /* le masquage ne doit jamais empecher la remontee d'une erreur */
  }
}

export function startMonitoring(app, router) {
  if (isNotDevelopmentEnv && isNotCrawlerBot) {
    import('@sentry/vue').then((Sentry) => {
      SentryInstance = Sentry
      Sentry.init({
        app,
        dsn: config.sentry.dns,
        environment: config.sentry.env,
        release: `${config.app.name}@${config.app.version}`,

        integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],

        captureUnhandledRejections: false,

        tracePropagationTargets: [new URL(config.urls.api).hostname, new URL(config.urls.hooks).hostname],

        tracesSampleRate: config.sentry.env === 'production' ? 0.2 : 1,

        tracingOptions: {
          trackComponents: true,
        },

        replaysSessionSampleRate: config.sentry.env === 'production' ? 0.01 : 0,
        replaysOnErrorSampleRate: config.sentry.env === 'production' ? 0.05 : 0,

        // Vue specific
        logErrors: true,
        attachProps: true,
        attachStacktrace: true,

        ignoreErrors: [
          /Navigation cancelled from "(.*)" to "(.*)" with a new navigation\./,
          /Redirected when going from "\/(.*)" to "\/(.*)" via a navigation guard\./,
          /Avoided redundant navigation to current location: "(.*)"\./,
          /Redirected when going from "(.*)" to "(.*)" via a navigation guard\./,
          'ChunkLoadError',
          'Loading CSS chunk',
          /Failed to fetch dynamically imported module(.*)/,
          /Unable to preload CSS(.*)/,
          'Non-Error promise rejection captured',
          /Non-Error promise rejection captured with (.*)/,
          /(.*)Failed to register a ServiceWorker(.*)/,
          /Object captured as promise rejection with keys(.*)/,
        ],

        // eslint-disable-next-line no-unused-vars
        beforeSend: (event, hint) => {
          // Les pages de vente transmettent l'adresse e-mail du visiteur dans l'URL
          // (parametre `email`, relu par le middleware pour pre-remplir l'inscription).
          // Sentry attache l'URL a chaque evenement : sans ce nettoyage, cette donnee
          // personnelle partirait chez un tiers a chaque erreur.
          maskPersonalData(event)

          const patternToIgnore = ['chrome-extension://', 'jimo-invader', '"filename":"/ekr/snippet.js"']
          const stringException = JSON.stringify(event.exception)
          if (event && event.exception) {
            for (const pattern of patternToIgnore) {
              if (stringException.includes(pattern)) {
                return null
              }
            }
          }

          return event
        },
      })
    })
  }
}

// keycloak-js rejette avec un objet simple ({ error, error_description }), parfois avec une
// chaine, parfois sans rien. Sentry ecarte explicitement ces rejets via la regle
// « Non-Error promise rejection captured » de `ignoreErrors`, filtre applique AVANT beforeSend
// et donc impossible a contourner ensuite. Sans cette normalisation en vraie Error, la panne
// serait envoyee puis jetee en silence, et on croirait a tort qu'il n'y a rien a voir.
function toStartupError(cause, etape) {
  if (cause instanceof Error) {
    return cause
  }

  let detail

  try {
    detail = typeof cause === 'string' ? cause : JSON.stringify(cause)
  } catch (e) {
    detail = null
  }

  if (!detail || detail === '{}') {
    detail = String(cause)
  }

  const error = new Error(`Echec de demarrage a l'etape "${etape}" : ${detail}`)

  error.name = 'StartupError'

  return error
}

// Panne au demarrage de l'application. Elle etait jusqu'ici avalee par un simple console.log :
// personne ne pouvait donc la voir, alors qu'elle empeche l'application entiere de se lancer.
// Sentry pouvant ne pas avoir fini de se charger a cet instant, on l'attend au besoin.
export function reportStartupIssue(cause, etape) {
  const error = toStartupError(cause, etape)
  const details = { tags: { startup_step: etape }, level: 'error' }

  try {
    if (SentryInstance) {
      SentryInstance.captureException(error, details)
    } else if (isNotDevelopmentEnv && isNotCrawlerBot) {
      // Sentry est charge par un import() non attendu : au moment d'une panne de demarrage
      // l'instance est souvent encore absente. On le charge alors nous-memes.
      import('@sentry/vue').then((Sentry) => Sentry.captureException(error, details)).catch(() => {})
    }
  } catch (e) {
    /* la remontee d'erreur ne doit jamais provoquer une seconde erreur */
  }

  // Trace locale utile au support, meme quand Sentry est indisponible.
  // eslint-disable-next-line no-console
  console.error(error)
}

export function setMonitoringUserId(userId, username) {
  if (isNotDevelopmentEnv && SentryInstance) {
    SentryInstance.setUser({ userId, username })
  }
}

export function clearMonitoringUserId() {
  if (isNotDevelopmentEnv && SentryInstance) {
    SentryInstance.setUser(null)
  }
}
